import { getPropheticDeclaration } from "@/api/propheticDeclaration";
import { formatLongDate } from "@/lib/dates";
import { useResource } from "@/hooks/useResource";
import { PROPHETIC_FALLBACK } from "@/api/normalize";
import { Container, Eyebrow, Skeleton } from "@/components/ui/Primitives";
import { Reveal } from "@/components/motion/Reveal";
import { AudioPlayer } from "@/components/media/AudioPlayer";
import { Icon } from "@/components/ui/Icon";

/**
 * The Prophetic Declaration, backend-driven (GET /api/prophetic-declaration). The service never
 * throws; with no declaration it returns a placeholder, and the player shows "coming soon".
 */
export function PropheticDeclaration() {
  const resource = useResource("prophetic-declaration", (signal) => getPropheticDeclaration(signal), { cacheMs: 10 * 60_000 });
  const d = resource.data ?? (resource.status === "error" ? PROPHETIC_FALLBACK : null);

  return (
    <section id="prophetic-declaration" className="declaration" aria-labelledby="declaration-title">
      <Container>
        <Reveal className="declaration__card" variant="scale">
          <div className="declaration__glow" aria-hidden />
          <span className="declaration__mark" aria-hidden>
            <Icon name="quote" size={120} />
          </span>
          <div className="declaration__head">
            <Eyebrow index="Audio">Prophetic Declaration</Eyebrow>
            {d ? (
              <>
                <h2 id="declaration-title" className={`declaration__title${d.isFallback || d.title.toLowerCase() === "prophetic declaration" ? " declaration__title--brand" : ""}`}>
                  {d.isFallback || d.title.toLowerCase() === "prophetic declaration" ? (
                    <>
                      Prophetic
                      <br />
                      <em>Declaration</em>
                    </>
                  ) : (
                    d.title
                  )}
                </h2>
                <p className="declaration__speaker">
                  <span className="declaration__speaker-line" aria-hidden />
                  {d.speaker}
                  {d.date ? <span className="declaration__date"> · {formatLongDate(d.date)}</span> : null}
                </p>
                {d.description ? <p className="declaration__desc">{d.description}</p> : null}
              </>
            ) : (
              <div aria-hidden>
                <Skeleton className="skeleton--title" />
                <Skeleton className="skeleton--line skeleton--short" />
              </div>
            )}
          </div>
          <div className="declaration__player">
            {d ? (
              <AudioPlayer src={d.audioUrl} title={d.isFallback ? "Prophetic Declaration" : d.title} subtitle={d.speaker} variant="feature" bars={72} />
            ) : (
              <Skeleton className="skeleton--player" />
            )}
            {d?.isFallback && !d.audioUrl ? <p className="declaration__soon">Today's prophetic declaration will appear here soon.</p> : null}
          </div>
        </Reveal>
      </Container>
    </section>
  );
}
